import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { FaTrash, FaPlus, FaExclamationTriangle, FaCalendarAlt, FaUserShield, FaFilter, FaSearch } from 'react-icons/fa';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../Components/Redux/store';
import { setAuth } from '../Components/Redux/authSlice';
import 'bootstrap/dist/css/bootstrap.min.css';

interface Class {
  id: number;
  name: string;
}

interface Section {
  id: number;
  name: string;
  class_associated: number;
}

interface Student {
  id: number;
  name: string;
  section_associated: number;
}

interface Discipline {
  id: number;
  class_name: number;
  section: number;
  student: number;
  date_of_incident: string;
  case: string;
  description: string;
  reported_by: string;
}

const DisciplineComponent: React.FC = () => {
  const [disciplines, setDisciplines] = useState<Discipline[]>([]);
  const [classes, setClasses] = useState<Class[]>([]);
  const [allSections, setAllSections] = useState<Section[]>([]);
  const [allStudents, setAllStudents] = useState<Student[]>([]);
  const [selectedClass, setSelectedClass] = useState<number | null>(null);
  const [selectedSection, setSelectedSection] = useState<number | null>(null);
  const [selectedStudent, setSelectedStudent] = useState<number | null>(null);
  const [userRole, setUserRole] = useState<string | null>(null);
  const dispatch = useDispatch();
  const auth = useSelector((state: RootState) => state.auth.value);
  const [formData, setFormData] = useState<Omit<Discipline, 'id'>>({
    class_name: 0,
    section: 0,
    student: 0,
    date_of_incident: '',
    case: '',
    description: '',
    reported_by: '',
  });
  const [showModal, setShowModal] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [filterClass, setFilterClass] = useState<string>('');

  const getCookie = (name: string) => {
    const cookieName = name + "=";
    const decodedCookie = decodeURIComponent(document.cookie);
    const cookieArray = decodedCookie.split(';');
    for (let i = 0; i < cookieArray.length; i++) {
      let cookie = cookieArray[i].trim();
      if (cookie.indexOf(cookieName) === 0) {
        return cookie.substring(cookieName.length, cookie.length);
      }
    }
    return '';
  };

  useEffect(() => {
    const fetchUserData = async () => {
      setLoading(true);
      try {
        const token = getCookie('token');
        if (!token) {
          dispatch(setAuth(false));
          return;
        }
        axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
        const response = await axios.get('/users/user');
        setUserRole(response.data.user.role);
        dispatch(setAuth(true));
      } catch (error) {
        console.error('Error fetching user data:', error);
        dispatch(setAuth(false));
        setError('Failed to fetch user data');
      } finally {
        setLoading(false);
      }
    };

    fetchUserData();
  }, [dispatch]);

  useEffect(() => {
    const fetchClasses = async () => {
      try {
        const response = await axios.get('/Branches/classes/');
        setClasses(response.data);
      } catch (err) {
        console.error('Error fetching classes:', err);
        setError('Error fetching classes');
      }
    };

    fetchClasses();
  }, []);

  useEffect(() => {
    const fetchAllSections = async () => {
      try {
        const response = await axios.get('/Branches/sections/');
        setAllSections(response.data);
      } catch (err) {
        console.error('Error fetching sections:', err);
        setError('Error fetching sections');
      }
    };

    fetchAllSections();
  }, []);

  useEffect(() => {
    const fetchAllStudents = async () => {
      try {
        const response = await axios.get('/Branches/students/');
        setAllStudents(response.data.results);
      } catch (err) {
        console.error('Error fetching students:', err);
        setError('Error fetching students');
      }
    };

    fetchAllStudents();
  }, []);

  useEffect(() => {
    const fetchDisciplines = async () => {
      try {
        const response = await axios.get('/Branches/disciplines/');
        setDisciplines(response.data);
      } catch (err) {
        setError('Error fetching discipline data');
      } finally {
        setLoading(false);
      }
    };

    fetchDisciplines();
  }, []);

  const filteredSections = selectedClass
    ? allSections.filter((s) => s.class_associated === selectedClass)
    : [];

  const filteredStudents = selectedSection
    ? allStudents.filter((s) => s.section_associated === selectedSection)
    : [];

  const handleClassChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const classId = parseInt(e.target.value) || 0;
    setSelectedClass(classId);
    setSelectedSection(null);
    setSelectedStudent(null);
    setFormData({ ...formData, class_name: classId, section: 0, student: 0 });
  };

  const handleSectionChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const sectionId = parseInt(e.target.value) || 0;
    setSelectedSection(sectionId);
    setSelectedStudent(null);
    setFormData({ ...formData, section: sectionId, student: 0 });
  };

  const handleStudentChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const studentId = parseInt(e.target.value) || 0;
    setSelectedStudent(studentId);
    setFormData({ ...formData, student: studentId });
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.class_name || !formData.section || !formData.student || !formData.date_of_incident || !formData.case || !formData.description || !formData.reported_by) {
      setError('Dhamaan meelaha waa loo baahan yahay');
      return;
    }
    try {
      const payload = { ...formData };
      const response = await axios.post('/Branches/disciplines/', payload);
      setDisciplines([...disciplines, response.data]);
      setSuccessMessage('Diiwaanka anshaxa si guul leh ayaa loo sameeyay');
      setError(null);
      setShowModal(false);
      setFormData({
        class_name: 0,
        section: 0,
        student: 0,
        date_of_incident: '',
        case: '',
        description: '',
        reported_by: '',
      });
      setSelectedClass(null);
      setSelectedSection(null);
      setSelectedStudent(null);
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (error) {
      console.error('Error creating discipline:', error);
      setError('Error creating discipline record');
    }
  };

  const handleDelete = async (id: number) => {
    if (window.confirm('Ma hubtaa inaad rabto inaad tirtirto diiwaankan anshaxa?')) {
      try {
        await axios.delete(`/Branches/disciplines/${id}/`);
        setDisciplines(disciplines.filter((discipline) => discipline.id !== id));
        setSuccessMessage('Diiwaanka anshaxa si guul leh ayaa loo tirtiray');
        setError(null);
        setTimeout(() => setSuccessMessage(null), 3000);
      } catch (error) {
        console.error('Error deleting discipline:', error);
        setError('Error deleting discipline record');
      }
    }
  };

  const getClassNameById = (id: number) => {
    const cls = classes.find((c) => c.id === id);
    return cls ? cls.name : 'Unknown Class';
  };

  const getSectionNameById = (id: number) => {
    const sec = allSections.find((s) => s.id === id);
    return sec ? sec.name : 'Unknown Section';
  };

  const getStudentNameById = (id: number) => {
    const stu = allStudents.find((s) => s.id === id);
    return stu ? stu.name : 'Unknown Student';
  };

  // Filter disciplines
  const filteredDisciplines = disciplines.filter(discipline => {
    const matchesSearch = searchTerm === '' ||
      getStudentNameById(discipline.student).toLowerCase().includes(searchTerm.toLowerCase()) ||
      discipline.case.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesClass = filterClass === '' || discipline.class_name.toString() === filterClass;

    return matchesSearch && matchesClass;
  });

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ height: '70vh' }}>
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  if (!auth) {
    return (
      <div className="text-center mt-5">
        <h4 className="text-muted">Ma aadan xaqiijin</h4>
        <a href="/login" className="btn btn-primary mt-3">
          Aad Login
        </a>
      </div>
    );
  }

  return (
    <div className="dashboard-content">
      <div className="container-fluid px-4">
        {/* Header */}
        <div className="row mb-4">
          <div className="col-12">
            <div className="d-flex flex-column flex-lg-row justify-content-between align-items-start align-items-lg-center gap-3">
              <div>
                <h1 className="h2 fw-bold text-dark mb-2">⚠️ Kiisaska Anshaxa</h1>
                <p className="text-muted mb-0">Maamul oo la soco diiwaanka anshaxa ardayda</p>
              </div>
              {userRole === 'admin' && (
                <button
                  className="btn btn-primary btn-lg rounded-pill px-4 shadow"
                  onClick={() => setShowModal(true)}
                >
                  <FaPlus className="me-2" />
                  Kiis Cusub
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Alerts */}
        {successMessage && (
          <div className="row mb-4">
            <div className="col-12">
              <div className="alert alert-success alert-dismissible fade show border-0 shadow-sm" role="alert">
                <i className="bi bi-check-circle-fill me-2"></i>
                {successMessage}
                <button type="button" className="btn-close" onClick={() => setSuccessMessage(null)}></button>
              </div>
            </div>
          </div>
        )}
        {error && (
          <div className="row mb-4">
            <div className="col-12">
              <div className="alert alert-danger alert-dismissible fade show border-0 shadow-sm" role="alert">
                <i className="bi bi-exclamation-triangle-fill me-2"></i>
                {error}
                <button type="button" className="btn-close" onClick={() => setError(null)}></button>
              </div>
            </div>
          </div>
        )}

        {/* Statistics Cards */}
        <div className="row g-3 mb-4">
          <div className="col-md-3">
            <div className="card border-0 shadow-sm bg-danger bg-opacity-10">
              <div className="card-body">
                <div className="d-flex justify-content-between align-items-center">
                  <div>
                    <p className="text-danger fw-semibold mb-1 small">Kiisaska Guud</p>
                    <h3 className="fw-bold mb-0 text-danger">{disciplines.length}</h3>
                  </div>
                  <div className="bg-danger bg-opacity-25 rounded-circle p-3">
                    <FaExclamationTriangle className="text-danger" size={24} />
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="col-md-3">
            <div className="card border-0 shadow-sm bg-warning bg-opacity-10">
              <div className="card-body">
                <div className="d-flex justify-content-between align-items-center">
                  <div>
                    <p className="text-warning fw-semibold mb-1 small">Bishaan</p>
                    <h3 className="fw-bold mb-0 text-warning">
                      {disciplines.filter(d => new Date(d.date_of_incident).getMonth() === new Date().getMonth()).length}
                    </h3>
                  </div>
                  <div className="bg-warning bg-opacity-25 rounded-circle p-3">
                    <FaCalendarAlt className="text-warning" size={24} />
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="col-md-3">
            <div className="card border-0 shadow-sm bg-info bg-opacity-10">
              <div className="card-body">
                <div className="d-flex justify-content-between align-items-center">
                  <div>
                    <p className="text-info fw-semibold mb-1 small">Toddobaadkan</p>
                    <h3 className="fw-bold mb-0 text-info">
                      {disciplines.filter(d => {
                        const weekAgo = new Date();
                        weekAgo.setDate(weekAgo.getDate() - 7);
                        return new Date(d.date_of_incident) >= weekAgo;
                      }).length}
                    </h3>
                  </div>
                  <div className="bg-info bg-opacity-25 rounded-circle p-3">
                    <FaUserShield className="text-info" size={24} />
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="col-md-3">
            <div className="card border-0 shadow-sm bg-primary bg-opacity-10">
              <div className="card-body">
                <div className="d-flex justify-content-between align-items-center">
                  <div>
                    <p className="text-primary fw-semibold mb-1 small">La Shaandheeyay</p>
                    <h3 className="fw-bold mb-0 text-primary">{filteredDisciplines.length}</h3>
                  </div>
                  <div className="bg-primary bg-opacity-25 rounded-circle p-3">
                    <FaFilter className="text-primary" size={24} />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Filters and Table */}
        <div className="row">
          <div className="col-12">
            <div className="card border-0 shadow-sm">
              <div className="card-body p-4">
                {/* Filters */}
                <div className="row g-3 mb-4">
                  <div className="col-lg-6">
                    <label className="form-label fw-semibold text-secondary small">Raadi</label>
                    <div className="input-group input-group-lg">
                      <span className="input-group-text bg-white border-2">
                        <FaSearch className="text-muted" />
                      </span>
                      <input
                        type="text"
                        className="form-control border-2"
                        placeholder="Ku raadi magaca ardayga ama kiiska..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                      />
                    </div>
                  </div>
                  <div className="col-lg-6">
                    <label className="form-label fw-semibold text-secondary small">Fasalka ku Shaandhee</label>
                    <select
                      className="form-select form-select-lg border-2"
                      value={filterClass}
                      onChange={(e) => setFilterClass(e.target.value)}
                    >
                      <option value="">Dhamaan Fasallada</option>
                      {classes.map(cls => (
                        <option key={cls.id} value={cls.id}>{cls.name}</option>
                      ))}
                    </select>
                  </div>
                </div>

                {/* Table */}
                <div className="table-responsive">
                  <table className="table table-hover align-middle mb-0">
                    <thead className="bg-light">
                      <tr>
                        <th style={{ width: '50px' }} className="text-muted fw-semibold small">#</th>
                        <th className="text-muted fw-semibold small">TAARIIKH</th>
                        <th className="text-muted fw-semibold small">ARDAYGA</th>
                        <th className="text-muted fw-semibold small">FASALKA</th>
                        <th className="text-muted fw-semibold small">QEYBTA</th>
                        <th className="text-muted fw-semibold small">KIISKA</th>
                        <th className="text-muted fw-semibold small">FAAHFAAHIN</th>
                        <th className="text-muted fw-semibold small">SOO SHEEGAY</th>
                        {userRole === 'admin' && <th style={{ width: '120px' }} className="text-end text-muted fw-semibold small">FALALKA</th>}
                      </tr>
                    </thead>
                    <tbody>
                      {filteredDisciplines.length === 0 ? (
                        <tr>
                          <td colSpan={userRole === 'admin' ? 9 : 8} className="text-center py-5">
                            <div className="text-muted">
                              <FaExclamationTriangle size={48} className="mb-3 opacity-50" />
                              <p className="mb-0">Diiwaan anshax lama helin</p>
                            </div>
                          </td>
                        </tr>
                      ) : (
                        filteredDisciplines.map((discipline, index) => (
                          <tr key={discipline.id}>
                            <td className="text-muted small fw-bold">{index + 1}</td>
                            <td className="text-muted small">{new Date(discipline.date_of_incident).toLocaleDateString()}</td>
                            <td className="fw-semibold text-dark">{getStudentNameById(discipline.student)}</td>
                            <td className="text-muted small">{getClassNameById(discipline.class_name)}</td>
                            <td className="text-muted small">{getSectionNameById(discipline.section)}</td>
                            <td>
                              <span className="badge bg-danger bg-opacity-10 text-danger rounded-pill px-3 py-2">
                                {discipline.case}
                              </span>
                            </td>
                            <td className="text-muted small" style={{ maxWidth: '300px' }}>
                              <div className="text-truncate" title={discipline.description}>
                                {discipline.description}
                              </div>
                            </td>
                            <td className="text-muted small">{discipline.reported_by}</td>
                            {userRole === 'admin' && (
                              <td className="text-end">
                                <button
                                  className="btn btn-danger btn-sm rounded-pill px-3"
                                  onClick={() => handleDelete(discipline.id)}
                                >
                                  <FaTrash size={12} className="me-1" />
                                  Tirtir
                                </button>
                              </td>
                            )}
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Modal */}
      {showModal && userRole === 'admin' && (
        <div
          className="modal fade show"
          style={{ display: 'block', backgroundColor: 'rgba(0, 0, 0, 0.5)' }}
          onClick={() => setShowModal(false)}
        >
          <div className="modal-dialog modal-dialog-centered modal-lg" onClick={(e) => e.stopPropagation()}>
            <div className="modal-content border-0 shadow-lg">
              <div className="modal-header bg-danger text-white border-0">
                <h5 className="modal-title fw-bold">
                  <FaExclamationTriangle className="me-2" />
                  Samee Kiis Anshax
                </h5>
                <button type="button" className="btn-close btn-close-white" onClick={() => setShowModal(false)}></button>
              </div>
              <div className="modal-body p-4">
                <form onSubmit={handleSubmit}>
                  <div className="row g-3">
                    <div className="col-md-6">
                      <label className="form-label fw-semibold text-secondary small">Fasalka *</label>
                      <select
                        className="form-select form-select-lg border-2"
                        value={selectedClass || ''}
                        onChange={handleClassChange}
                        required
                      >
                        <option value="">Dooro Fasal</option>
                        {classes.map((cls) => (
                          <option key={cls.id} value={cls.id}>
                            {cls.name}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="col-md-6">
                      <label className="form-label fw-semibold text-secondary small">Qeybta *</label>
                      <select
                        className="form-select form-select-lg border-2"
                        value={selectedSection || ''}
                        onChange={handleSectionChange}
                        required
                        disabled={!selectedClass}
                      >
                        <option value="">Dooro Qeyb</option>
                        {filteredSections.map((section) => (
                          <option key={section.id} value={section.id}>
                            {section.name}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="col-md-6">
                      <label className="form-label fw-semibold text-secondary small">Ardayga *</label>
                      <select
                        className="form-select form-select-lg border-2"
                        value={selectedStudent || ''}
                        onChange={handleStudentChange}
                        required
                        disabled={!selectedSection}
                      >
                        <option value="">Dooro Arday</option>
                        {filteredStudents.map((student) => (
                          <option key={student.id} value={student.id}>
                            {student.name}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="col-md-6">
                      <label className="form-label fw-semibold text-secondary small">Taariikhda Dhacdada *</label>
                      <input
                        type="date"
                        className="form-control form-control-lg border-2"
                        name="date_of_incident"
                        value={formData.date_of_incident}
                        onChange={handleInputChange}
                        required
                      />
                    </div>
                    <div className="col-12">
                      <label className="form-label fw-semibold text-secondary small">Nooca Kiiska *</label>
                      <input
                        type="text"
                        className="form-control form-control-lg border-2"
                        name="case"
                        value={formData.case}
                        onChange={handleInputChange}
                        placeholder="tusaale, Dagaal, Qish, Daahitaan"
                        required
                      />
                    </div>
                    <div className="col-12">
                      <label className="form-label fw-semibold text-secondary small">Faahfaahin *</label>
                      <textarea
                        className="form-control form-control-lg border-2"
                        name="description"
                        value={formData.description}
                        onChange={handleInputChange}
                        rows={4}
                        placeholder="Bixi faahfaahin dheeraad ah oo ku saabsan dhacdada..."
                        required
                      />
                    </div>
                    <div className="col-12">
                      <label className="form-label fw-semibold text-secondary small">Waxaa soo sheegay *</label>
                      <input
                        type="text"
                        className="form-control form-control-lg border-2"
                        name="reported_by"
                        value={formData.reported_by}
                        onChange={handleInputChange}
                        placeholder="Magaca qofka soo sheegay"
                        required
                      />
                    </div>
                  </div>
                  <div className="d-flex justify-content-end gap-2 mt-4 pt-3 border-top">
                    <button
                      type="button"
                      className="btn btn-secondary btn-lg rounded-pill px-4"
                      onClick={() => setShowModal(false)}
                    >
                      Ka Noqo
                    </button>
                    <button
                      type="submit"
                      className="btn btn-danger btn-lg rounded-pill px-4 shadow"
                    >
                      <FaPlus className="me-2" />
                      Samee Kiis
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DisciplineComponent;